import {
  Box,
  Card,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Tab,
  Typography,
  Hidden,
  IconButton,
  Paper,
  Table,
  TableSortLabel,
  TableContainer,
  TableCell,
  TableRow,
  TableBody,
  TableHead,
} from '@material-ui/core';
import {
  CustomSelect,
  CustomTextField,
  MapPlannerPreview,
  ProjectListItem,
} from '@uml/uml-components';
import { FormProvider, useForm } from 'react-hook-form';
import React, { useEffect } from 'react';
import { makeStyles, withStyles, useTheme } from '@material-ui/core/styles';

import Button from '@material-ui/core/Button';
import MapIcon from '@material-ui/icons/Map';
import ListIcon from '@material-ui/icons/List';
import Pagination from '@material-ui/lab/Pagination';
import PropTypes from 'prop-types';
import ReactDOM from 'react-dom';
import Tabs from '@material-ui/core/Tabs';
import ViewModuleIcon from '@material-ui/icons/ViewModule';
import Wrapper from '../components/Wrapper';
import api from '../utilities/api';
import { getDistricts } from '../services/projects';
import ProjectStatusLabel from '../components/ProjectStatusLabel';
import ProjectItemCard from '../components/ProjectItemCard';
import VisibilityIcon from '@material-ui/icons/Visibility';

const useStyles = makeStyles((theme) => ({
  root: {
    position: 'relative',
    width: '100%',

    '&>div': {
      width: '100%',
    },
  },
  projectsList: {
    display: 'grid',
    gridTemplateColumns: '1fr',
    marginTop: theme.spacing(2),
    '@media(min-width: 768px)': {
      gridTemplateColumns: '1fr 1fr',
      gap: 24,
    },
    '@media(min-width: 1200px)': {
      gridTemplateColumns: '1fr 1fr 1fr',
    },
  },
  projectsListItem: {
    position: 'relative',
    marginBottom: theme.spacing(2),
    padding: theme.spacing(2),
    '@media(min-width: 768px)': {
      marginBottom: 0,
      paddingBottom: 50,
    },
  },
  filterContainer: {
    '@media(min-width: 768px)': {
      maxWidth: '80%',
      display: 'inline-flex',
    },
  },
  filterForm: {
    '@media(min-width: 768px)': {
      display: 'flex',
    },
  },
  searchButton: {
    marginTop: 16,

    '@media(min-width: 768px)': {
      margin: 'auto',
    },
  },
  projectDetailButton: {
    '@media(min-width: 768px)': {
      position: 'absolute',
      bottom: 16,
      left: 16,
    },
  },
  projectsListItemTitle: {
    wordBreak: 'break-word',
    marginBottom: theme.spacing(2),
    '@media(min-width: 768px)': {
      height: 62,
    },
  },
  projectListDetail: {
    marginBottom: theme.spacing(2),
    fontSize: '1.3em',
  },
  statusLabel: {
    display: 'flex',
    alignItems: 'center',
  },
  statusLabelWrap: {
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(2),
    height: 32,
  },
  projectItemDescription: {
    fontSize: '1.2em',
    marginBottom: theme.spacing(2),
  },
  withMarginBottom: {
    marginBottom: theme.spacing(2),
  },
  withMarginBottomMobile: {
    marginBottom: 0,
    padding: '0 !Important',

    '@media(min-width: 768px)': {
      marginBottom: 0,
      marginTop: 0,
      padding: '0 8px !important',
    },
  },
  projectsCount: {
    alignSelf: 'center',
  },
  projectsDetailRow: {
    margin: '10px 0 16px 0',
  },
  pagination: {
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
    paddingTop: theme.spacing(2),
  },
  likeH2Size: {
    fontSize: '3rem',
    fontWeight: 400,
  },
  likeH5Size: {
    fontSize: '1.5rem',
    fontWeight: 400,
  },
  likeH6Size: {
    fontSize: '1.5rem',
    fontWeight: 400,
  },
  MenuItemroot: {
    wordBreak: 'break-all',
    wordWrap: 'break-word',
    whiteSpace: 'normal',
  },
  disabled: {
    backgroundColor: theme.palette.input.disabled,
  },
  statusBar: {
    [theme.breakpoints.up('md')]: {
      marginRight: 15,
    },
  },
  rowReverse: {
    flexDirection: 'row-reverse',
  },
  projectsType: {
    width: '100%',
    justifyContent: 'center',
    margin: '10px auto',
  },
  fullWidth: {
    width: '100%',
    '&:focus': {
      backgroundColor: theme.palette.primary.dark,
      color: '#FFFFFF',
    },
  },
}));

const AntTab = withStyles((theme) => ({
  root: {
    '&:focus': {
      backgroundColor: theme.palette.primary.contrastText,
    },
  },
  // eslint-disable-next-line react/jsx-props-no-spreading
}))((props) => <Tab disableRipple {...props} />);

const AntPagination = withStyles((theme) => ({
  root: {
    '& .MuiPaginationItem-page.Mui-selected': {
      backgroundColor: 'white !important',
      border: '1px solid #0069B4',
      color: theme.palette.action.active,
    },
  },
  // eslint-disable-next-line react/jsx-props-no-spreading
}))((props) => <Pagination {...props} />);

const PROJECT_STATUS = {
  bo: [
    'Oceniony pozytywnie',
    'Odrzucony',
    'Do głosowania',
    'Wybrany do realizacji',
    'Niewybrany do realizacji',
  ],
  bz: [
    'Ocena pozytywna',
    'Ocena negatywna',
    'Wybrany do realizacji',
    'Niewybrany do realizacji',
  ],
};

const BUDGET_NAME = {
  bo: 'Budżetu Obywatelskiego',
  bz: 'Zielonego Budżetu',
};

const SORT_TYPES = [
  {
    name: 'Numer projektu: rosnąco',
    value: 'sorted_abbreviation,sorted_number_integer',
  },
  {
    name: 'Numer projektu: malejąco',
    value: '-sorted_abbreviation,-sorted_number_integer',
  },
];

const ProjectsList = ({ editionId }) => {
  const classes = useStyles();
  const [value, setValue] = React.useState(0);
  const methods = useForm();
  const [projects, setProjects] = React.useState([]);
  const [count, setCount] = React.useState(0);
  const [pageCount, setPageCount] = React.useState(0);
  const [range, setRange] = React.useState(
    service === 'bo' ? 'Dzielnicowy' : '',
  );
  const [category, setCategory] = React.useState('');
  const [district, setDistrict] = React.useState('');
  const [projectStatus, setProjectStatus] = React.useState('');
  const [limit, setLimit] = React.useState(15);
  const [page, setPage] = React.useState(1);
  const [searchTitle, setSearchTitle] = React.useState('');
  const [projectMarkers, setProjectMarkers] = React.useState([]);
  const [availableDistricts, setAvailableDistricts] = React.useState([]);
  const [ordering, setOrdering] = React.useState(SORT_TYPES[0].value);
  const [orderBy, setOrderBy] = React.useState('abbreviation,number_integer');
  const [orderType, setOrderType] = React.useState('asc');
  const [isMobile, setIsMobile] = React.useState(false);

  const TYPE = {
    bo: ['Ogólnomiejski', 'Dzielnicowy'],
    bz: ['wKoronachDrzew'],
  };
  const theme = useTheme();

  const handleResize = () => {
    setIsMobile(window.innerWidth < 960);
  };

  window.addEventListener('resize', handleResize);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const tempSort = orderBy.includes(',')
          ? orderBy
              .split(',')
              .map((elem) => `${orderType === 'desc' ? '-' : ''}sorted_${elem}`)
              .join(',')
          : `${orderType === 'desc' ? '-' : ''}${
              orderBy === 'votes_count' ? '' : 'sorted_'
            }${orderBy}`;

        const { data } = await api.get(
          `/${service}/projects/external-search/`,
          {
            params: {
              limit,
              page,
              ordering: isMobile || value !== 0 ? ordering : tempSort,
              polish_text: searchTitle,
              district,
              project_range: range,
              category: category,
              cms_status: projectStatus,
              edition__contains: editionId,
            },
          },
        );
        setProjects(data.results);
        const projectMarkers = data.results.map((project) => {
          return {
            projectId: project.id,
            markers: project?.project_markers ?? '',
          };
        });
        setProjectMarkers(projectMarkers);
        setCount(data.count);
      } catch (e) {
        console.error(e);
      }
    };
    if (editionId !== '') {
      fetchData();
    }
  }, [
    limit,
    page,
    searchTitle,
    district,
    range,
    projectStatus,
    editionId,
    ordering,
    orderBy,
    orderType,
  ]);

  useEffect(() => {
    const newPageCount = Math.ceil(parseInt(count, 10) / parseInt(limit, 10));
    setPageCount(newPageCount);
  }, [count, limit]);

  useEffect(() => {
    const fetchDistricts = async () => {
      const data = await getDistricts();
      const districts = await data.json();
      const choices = districts?.features ?? [];
      setAvailableDistricts(
        choices.map((choice) => ({
          id: choice.id,
          name: choice.properties.nazwa,
        })),
      );
    };
    fetchDistricts();
  }, []);

  const onFilterSubmit = (data) => {
    setSearchTitle(data.text);
    setRange(data.projectRange);
    setCategory(data.category);
    setDistrict(data.district);
    setProjectStatus(data.project_status);
  };

  const handleLimit = (e) => {
    setLimit(e.target.value);
  };

  const changeRange = (value) => {
    setProjectStatus('');
    setDistrict('');
    setRange(value);
  };

  const handleChangeTab = (event, newValue) => {
    setValue(newValue);
  };

  // eslint-disable-next-line no-shadow
  const handlePagination = (event, value) => {
    setPage(value);
  };

  const onSort = (property) => {
    const isAsc = orderBy === property && orderType === 'asc';
    setOrderType(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const TabPanel = (props) => {
    // eslint-disable-next-line react/prop-types, no-shadow
    const { children, value, index, ...other } = props;
    return (
      <div
        role='tabpanel'
        hidden={value !== index}
        id={`full-width-tabpanel-${index}`}
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...other}
      >
        {value === index && <Box p={0}>{children}</Box>}
      </div>
    );
  };
  const headers = [
    {
      name: 'Numer projektu',
      id: 'abbreviation,number_integer',
      sortable: true,
    },
    { name: 'Nazwa', id: 'title', sortable: true },
    { name: 'Koszt po weryfikacji', id: 'estimate', sortable: true },
    service == 'bo' ? { name: 'Status', id: 'status', sortable: true } : [],
    { name: 'Akcje', id: 'actions', sortable: false },
  ];

  return (
    <Grid container className={classes.containerDiv}>
      <Grid item xs={12}>
        <Tabs
          value={value}
          onChange={handleChangeTab}
          indicatorColor='primary'
          textColor='primary'
          className={classes.withMarginBottom}
        >
          <AntTab
            label='Lista'
            aria-label='Rozwiń listę'
            icon={<ListIcon />}
            value={0}
          />
          <AntTab
            label='Kafelki'
            aria-label='Rozwiń kafelki'
            icon={<ViewModuleIcon />}
            value={1}
          />
          <AntTab
            label='Mapa'
            aria-label='Rozwiń mapę'
            icon={<MapIcon />}
            value={2}
          />
        </Tabs>
        <Card style={{ padding: 16 }} className={classes.withMarginBottom}>
          {/* eslint-disable-next-line react/jsx-props-no-spreading */}
          <FormProvider {...methods}>
            <form id='search-form' className={classes.filterForm}>
              <Grid container spacing={2} className={classes.filterContainer}>
                <Grid xs={12} item className={classes.withMarginBottomMobile}>
                  <div className={classes.root}>
                    <CustomTextField
                      size='small'
                      id='projectTitle'
                      label='Fraza do wyszukania'
                      variant='outlined'
                      name='text'
                    />
                  </div>
                </Grid>
                {service === 'bz' ? (
                  <>
                    <Grid
                      xs={12}
                      item
                      md={4}
                      className={classes.withMarginBottomMobile}
                    >
                      <CustomSelect
                        id='category'
                        label='Kategoria'
                        name='category'
                        size='small'
                        clearable
                      >
                        {TYPE['bz'].map((item) => (
                          <MenuItem key={item} value={item}>
                            {item}
                          </MenuItem>
                        ))}
                      </CustomSelect>
                    </Grid>
                    <Grid
                      xs={12}
                      item
                      md={4}
                      className={classes.withMarginBottomMobile}
                    >
                      <CustomSelect
                        id='projectStatus'
                        label='Status'
                        name='project_status'
                        size='small'
                        clearable
                      >
                        {PROJECT_STATUS['bz'].map((item) => (
                          <MenuItem key={item} value={item}>
                            {item}
                          </MenuItem>
                        ))}
                      </CustomSelect>
                    </Grid>
                    <Grid
                      xs={12}
                      item
                      md={4}
                      className={classes.withMarginBottomMobile}
                    >
                      <CustomSelect
                        id='district'
                        label='Dzielnica'
                        name='district'
                        size='small'
                        clearable
                      >
                        {availableDistricts
                          .sort((a, b) => a.name.localeCompare(b.name))
                          .map((item) => (
                            <MenuItem key={item.id} value={item.id}>
                              {item.name}
                            </MenuItem>
                          ))}
                      </CustomSelect>
                    </Grid>
                  </>
                ) : (
                  ''
                )}
              </Grid>

              <Button
                variant='contained'
                color='primary'
                type='submit'
                onClick={(e) => {
                  e.preventDefault();
                  onFilterSubmit(methods.getValues());
                }}
                className={classes.searchButton}
              >
                Szukaj
              </Button>
            </form>
          </FormProvider>
        </Card>
        {service === 'bz' ? (
          <div></div>
        ) : (
          <div>
            <Grid container spacing={2} className={classes.projectsType}>
              <Grid item xs={6} md={5}>
                <Button
                  variant={range === 'Dzielnicowy' ? 'contained' : 'outlined'}
                  size='large'
                  color='primary'
                  onClick={() => changeRange('Dzielnicowy')}
                  className={classes.fullWidth}
                >
                  Projekty dzielnicowe
                </Button>
              </Grid>
              <Grid item xs={6} md={5}>
                <Button
                  variant={range === 'Ogólnomiejski' ? 'contained' : 'outlined'}
                  size='large'
                  color='primary'
                  onClick={() => changeRange('Ogólnomiejski')}
                  className={classes.fullWidth}
                >
                  Projekty ogólnomiejskie
                </Button>
              </Grid>
            </Grid>
            <Grid container className={classes.rowReverse}>
              <Grid item xs={12} md={4}>
                <FormControl
                  variant='outlined'
                  fullWidth
                  margin='normal'
                  size='small'
                >
                  <InputLabel id='sort-project-label'>
                    Pokaż dzielnice
                  </InputLabel>
                  <Select
                    labelId='sort-district-select'
                    id='sort-district-select'
                    value={district}
                    label='Pokaż dzielnice'
                    onChange={(e) => setDistrict(e.target.value)}
                    style={{ width: '100%' }}
                    MenuProps={{
                      anchorOrigin: {
                        vertical: 'bottom',
                        horizontal: 'left',
                      },
                      getContentAnchorEl: null,
                      PaperProps: {
                        style: {
                          maxHeight: 48 * 4.5 + 8,
                          maxWidth: 540,
                        },
                      },
                    }}
                  >
                    <MenuItem key='dzielnice.all' value=''>
                      Wszystkie
                    </MenuItem>
                    {availableDistricts
                      .sort((a, b) => a.name.localeCompare(b.name))
                      .map((item) => (
                        <MenuItem key={item.id} value={item.id}>
                          {item.name}
                        </MenuItem>
                      ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={4} className={classes.statusBar}>
                <FormControl
                  variant='outlined'
                  fullWidth
                  margin='normal'
                  size='small'
                >
                  <InputLabel id='filter-status-label'>Status</InputLabel>
                  <Select
                    labelId='filter-status-select'
                    id='filter-status-select'
                    value={projectStatus}
                    label='Status'
                    onChange={(e) => setProjectStatus(e.target.value)}
                    style={{ width: '100%' }}
                    MenuProps={{
                      anchorOrigin: {
                        vertical: 'bottom',
                        horizontal: 'left',
                      },
                      getContentAnchorEl: null,
                      PaperProps: {
                        style: {
                          maxHeight: 48 * 4.5 + 8,
                          maxWidth: 540,
                        },
                      },
                    }}
                  >
                    <MenuItem key='status.all' value=''>
                      Brak
                    </MenuItem>
                    {PROJECT_STATUS['bo'].map((item) => (
                      <MenuItem key={item} value={item}>
                        {item}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </div>
        )}

        <TabPanel value={value} index={0}>
          <Hidden only={['xs', 'sm']}>
            <Paper>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      {headers.map((header) => (
                        <>
                          {header.sortable ? (
                            <TableCell
                              key={header.id}
                              sortDirection={
                                orderBy === header.id ? orderType : false
                              }
                            >
                              <TableSortLabel
                                active={orderBy === header.id}
                                direction={
                                  orderBy === header.id ? orderType : 'asc'
                                }
                                onClick={() => onSort(header.id)}
                              >
                                {header.name}
                              </TableSortLabel>
                            </TableCell>
                          ) : (
                            <TableCell>{header.name}</TableCell>
                          )}
                        </>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {projects.map((item, idx) => {
                      const starColor =
                        item.status === 'Wybrany do realizacji'
                          ? theme.palette.success.dark
                          : theme.palette.primary.dark;
                      return (
                        <TableRow
                          className={
                            item.status === 'Wybrany do realizacji'
                              ? classes.wonRow
                              : ''
                          }
                        >
                          <TableCell>{item.number}</TableCell>
                          <TableCell>{item.title}</TableCell>
                          <TableCell>{item.estimate}</TableCell>
                          <TableCell>
                            <ProjectStatusLabel
                              status={item.status}
                              classes={classes}
                              theme={theme}
                            ></ProjectStatusLabel>
                          </TableCell>
                          <TableCell>
                            <>
                              <IconButton
                                href={`${previewUrl}?projectId=${item.id}`}
                                title='Podgląd'
                                aria-label='Podgląd'
                              >
                                <VisibilityIcon />
                              </IconButton>
                            </>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Hidden>
          <Hidden only={['md', 'lg', 'xl']}>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl
                variant='outlined'
                fullWidth
                margin='normal'
                size='small'
              >
                <InputLabel id='sort-project-label'>Sortowanie</InputLabel>
                <Select
                  labelId='sort-project-label'
                  id='sort-project'
                  value={ordering}
                  label='Sortowanie'
                  onChange={(e) => setOrdering(e.target.value)}
                  style={{ width: '100%' }}
                  MenuProps={{
                    anchorOrigin: {
                      vertical: 'bottom',
                      horizontal: 'left',
                    },
                    getContentAnchorEl: null,
                    PaperProps: {
                      style: {
                        maxHeight: 48 * 4.5 + 8,
                        maxWidth: 540,
                      },
                    },
                  }}
                >
                  {SORT_TYPES.map((item) => (
                    <MenuItem
                      key={item.name}
                      value={item.value}
                      classes={{ root: classes.MenuItemroot }}
                    >
                      {item.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            {projects.map((item) => (
              <ProjectItemCard item={item} />
            ))}
          </Hidden>
        </TabPanel>

        <TabPanel value={value} index={1}>
          <Grid
            container
            item
            xs={12}
            justifyContent='space-between'
            className={classes.projectsDetailRow}
          >
            <Grid item xs={12} sm={4} className={classes.projectsCount}>
              <Typography>Łącznie: {count} projektów</Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl
                variant='outlined'
                fullWidth
                margin='normal'
                size='small'
              >
                <InputLabel id='sort-project-label'>Sortowanie</InputLabel>
                <Select
                  labelId='sort-project-label'
                  id='sort-project'
                  value={ordering}
                  label='Sortowanie'
                  onChange={(e) => setOrdering(e.target.value)}
                  style={{ width: '100%' }}
                  MenuProps={{
                    anchorOrigin: {
                      vertical: 'bottom',
                      horizontal: 'left',
                    },
                    getContentAnchorEl: null,
                    PaperProps: {
                      style: {
                        maxHeight: 48 * 4.5 + 8,
                        maxWidth: 540,
                      },
                    },
                  }}
                >
                  {SORT_TYPES.map((item) => (
                    <MenuItem
                      key={item.name}
                      value={item.value}
                      classes={{ root: classes.MenuItemroot }}
                    >
                      {item.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
          <Grid item xs={12}>
            <Grid className={classes.projectsList}>
              {projects.map((project) => {
                return (
                  <ProjectListItem
                    key={project.id}
                    project={project}
                    currentService={service}
                  />
                );
              })}
            </Grid>
          </Grid>
        </TabPanel>
        <TabPanel value={value} index={2}>
          <Grid item xs={12}>
            <MapPlannerPreview
              projects={projects}
              previewData={projectMarkers}
              positionPreview
              withProjectPreview
              availableLayers={availableLayers}
            />
          </Grid>
        </TabPanel>
        {value !== 2 ? (
          <Grid container style={{ justifyContent: 'space-betweend' }}>
            <Grid item xs={12} md={2}>
              {/* eslint-disable-next-line react/jsx-props-no-spreading */}
              <FormProvider {...methods}>
                <form>
                  <CustomSelect
                    handleChange={(e) => handleLimit(e)}
                    variant='standard'
                    id='projectRange'
                    label='Pokazuj w tabeli'
                    name='count_show_item'
                    size='small'
                  >
                    {['15', '30', '60', '120'].map((item) => (
                      <MenuItem key={item} value={item}>
                        {item}
                      </MenuItem>
                    ))}
                  </CustomSelect>
                </form>
              </FormProvider>
            </Grid>
            <Grid item xs={12} md={10} className={classes.pagination}>
              <AntPagination
                onChange={handlePagination}
                count={pageCount}
                variant='outlined'
                shape='rounded'
              />
            </Grid>
          </Grid>
        ) : (
          ''
        )}
      </Grid>
    </Grid>
  );
};

ProjectsList.propTypes = {
  editionId: PropTypes.string.isRequired,
};

export default ProjectsList;

if (document.querySelector('#projectsList') !== null) {
  ReactDOM.render(
    <Wrapper productionPrefix='prol' seed='projects-list'>
      <ProjectsList editionId={edition} />
    </Wrapper>,
    document.getElementById('projectsList'),
  );
}
